import React, { useEffect, useState } from 'react';

import { Badge, Popover } from '@mui/material';
import { EllipsisVertical } from 'lucide-react';
import { SelectButton } from 'src/components';
import { useResponsiveOverflow } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';

/** Must match the toolbar row gap so measured widths line up with the rendered row. */
const CHIP_GAP_PX = 12;
/** A count badge sticks out past the right edge of the button it decorates. */
const BADGE_OVERHANG_PX = 8;
/** 18px icon + 8px padding + 1px border on each side. */
const OVERFLOW_BUTTON_PX = 36;
/** The usual 8px popover inset, plus room for the badge overhang. */
const OVERFLOW_POPOVER_PADDING_PX = 2 + BADGE_OVERHANG_PX;

const useStyles = makeStyles()(theme => ({
  filterBadge: {
    '& .MuiBadge-badge': {
      right: 2,
      top: 2,
    },
  },
  measureLayer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: CHIP_GAP_PX,
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: 'max-content',
  },
  /**
   * Item breakpoints only cover the gaps *between* chips, so the measured
   * overflow button has to carry the gap that precedes it in the real row.
   */
  measuredOverflowButton: {
    paddingLeft: CHIP_GAP_PX,
  },
  overflowButton: {
    flexShrink: 0,
    height: 32,
    padding: theme.spacing(1),
  },
  overflowPopoverPaper: {
    alignItems: 'center',
    borderRadius: 8,
    display: 'flex',
    flexWrap: 'wrap',
    /**
     * The chips keep their badges in here too, so the gap between them has to
     * clear the overhang just like the toolbar row does.
     */
    gap: CHIP_GAP_PX,
    // Keeps the menu off the button it opens from.
    marginTop: theme.spacing(0.5),
    padding: OVERFLOW_POPOVER_PADDING_PX,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: CHIP_GAP_PX,
    justifyContent: 'flex-start',
    /**
     * Collapsing to the overflow button is the narrowest useful state, so the
     * row stops shrinking there rather than clipping the button itself.
     */
    minWidth: OVERFLOW_BUTTON_PX + BADGE_OVERHANG_PX,
    /**
     * Measurement decides what renders; clipping only guards the frames before
     * a resize is measured. It happens at the padding edge, so the padding is
     * what keeps it off the count badges, and because the observed content box
     * excludes padding it doesn't skew the fit calculation.
     */
    overflow: 'hidden',
    paddingBlock: BADGE_OVERHANG_PX,
    paddingRight: BADGE_OVERHANG_PX,
    position: 'relative',
  },
}));

export interface WorkflowQuickFilterItem {
  activeCount: number;
  id: string;
  render: () => React.ReactNode;
}

interface WorkflowQuickFiltersProps {
  items: WorkflowQuickFilterItem[];
}

export function WorkflowQuickFilters({ items }: WorkflowQuickFiltersProps): JSX.Element {
  const { classes } = useStyles();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const { containerRef, getItemRef, measureRef, overflowRef, visibleCount } =
    useResponsiveOverflow<HTMLDivElement>(items.length, 'horizontal');
  const [overflowAnchorEl, setOverflowAnchorEl] = useState<HTMLButtonElement | null>(null);
  const visibleItems = items.slice(0, visibleCount);
  const overflowItems = items.slice(visibleCount);
  const showOverflow = overflowItems.length > 0;
  const overflowActiveCount = overflowItems.reduce((sum, item) => sum + item.activeCount, 0);

  /**
   * The open flag outlives the overflow button, which only mounts while items
   * overflow. Without this the menu would reopen on its own once a resize
   * brings the button back.
   */
  useEffect(() => {
    if (!showOverflow) setOverflowOpen(false);
  }, [showOverflow]);

  return (
    <div ref={containerRef} className={classes.root}>
      <div ref={measureRef} aria-hidden className={classes.measureLayer}>
        {items.map((item, index) => (
          <div key={item.id} ref={getItemRef(index)}>
            {item.render()}
          </div>
        ))}
        <div ref={overflowRef} className={classes.measuredOverflowButton}>
          <SelectButton className={classes.overflowButton} variant="outlined">
            <EllipsisVertical size={18} />
          </SelectButton>
        </div>
      </div>

      {visibleItems.map(item => (
        <React.Fragment key={item.id}>{item.render()}</React.Fragment>
      ))}

      {showOverflow && (
        <>
          <Badge
            badgeContent={overflowActiveCount > 0 ? overflowActiveCount : null}
            className={classes.filterBadge}
            color="primary"
          >
            <SelectButton
              ref={setOverflowAnchorEl}
              aria-label="More filters"
              className={classes.overflowButton}
              selected={overflowOpen}
              variant="outlined"
              onClick={() => setOverflowOpen(open => !open)}
            >
              <EllipsisVertical size={18} />
            </SelectButton>
          </Badge>
          <Popover
            anchorEl={overflowAnchorEl}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            open={overflowOpen}
            slotProps={{ paper: { className: classes.overflowPopoverPaper } }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            onClose={() => setOverflowOpen(false)}
          >
            {overflowItems.map(item => (
              <React.Fragment key={item.id}>{item.render()}</React.Fragment>
            ))}
          </Popover>
        </>
      )}
    </div>
  );
}
